import { useDetachCourseLessonFromGroup } from "hooks/api/courses/useDetachCourseLessonFromGroup";
import { useRole } from "hooks/common";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  LessonDetailsContent as Content,
  GroupClassroomContent,
} from "content";
import {
  useAddCourseLessonToGroup,
  useCourseLessonById,
  useCourseLessonChapters,
  useCourseLessons,
} from "hooks/api/courses";
import { useGroupById } from "hooks/api/groups";
import { useExerciseConstructor } from "hooks/redux/useExerciseConstructor";
import { useBreadcrumbs, useLessonSettings } from "hooks/redux";
import { Space } from "components/Common/Space";
import { Button } from "components/Interactive/Button";
import {
  ChaptersSidebar,
  HomeworkContent,
  HomeworkSidebar,
  LessonContainer,
  LessonContent,
  LessonSidebar,
} from "components/Lesson";
import { logError } from "utils/notifications";
import { Route } from "constants/routes";
import { ELessonContentType } from "types/api/lesson";

export const GroupClassroomCourseLesson: React.FC = () => {
  const { groupId, courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryChapterId = searchParams.get("chapterId");

  const { setPageName, resetPageName } = useBreadcrumbs();

  const [contentType, setContentType] = useState<ELessonContentType>(
    ELessonContentType.LESSON
  );
  const [activeChapterId, setActiveChapterId] = useState<number | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(
    null
  );
  const { isUserStudent } = useRole();

  const { data: group, isLoading: isGroupLoading } = useGroupById(
    groupId ? parseInt(groupId) : undefined
  );

  // Use selected student ID or fall back to first student ID from group
  const studentId =
    selectedStudentId?.toString() || group?.memberIds?.[0]?.toString();

  // Initialize selected student when group loads
  useEffect(() => {
    if (group?.memberIds?.length && !selectedStudentId) {
      setSelectedStudentId(group.memberIds[0]);
    }
  }, [group?.memberIds, selectedStudentId]);

  const { data: lessonChapters = [], isLoading: isChaptersLoading } =
    useCourseLessonChapters(courseId, lessonId);
  const {
    data: lesson,
    isLoading: isLessonLoading,
    error,
  } = useCourseLessonById(courseId, lessonId);

  // Only make student-specific API call when we have studentId from group
  const shouldSkipStudentQuery = !studentId || isGroupLoading;
  const { data: studentCourseLessonsData, isLoading: isStudentLessonsLoading } =
    useCourseLessons(
      shouldSkipStudentQuery ? undefined : courseId,
      studentId
        ? {
            studentId,
            pageSize: 100,
          }
        : undefined
    );
  const { mutate: addLessonToGroup } = useAddCourseLessonToGroup();
  const { mutate: detachLessonFromGroup } = useDetachCourseLessonFromGroup();

  const isLessonPinned: boolean = useMemo(() => {
    // Don't check pin status until we have group and student data
    if (!studentId || isGroupLoading || isStudentLessonsLoading) return false;

    return (
      studentCourseLessonsData?.content.some(
        (lesson) => lesson.id === Number(lessonId)
      ) || false
    );
  }, [
    studentCourseLessonsData?.content,
    lessonId,
    studentId,
    isGroupLoading,
    isStudentLessonsLoading,
  ]);

  const isLoading: boolean = useMemo(
    () =>
      isLessonLoading ||
      isChaptersLoading ||
      isStudentLessonsLoading ||
      isGroupLoading,
    [
      isLessonLoading,
      isChaptersLoading,
      isStudentLessonsLoading,
      isGroupLoading,
    ]
  );

  useEffect(() => {
    // TODO: handle properly not found resource error
    if (error) {
      navigate(Route.App.Materials.ROOT.path);
    }
  }, [error, navigate]);

  useEffect(() => {
    if (group?.name) {
      const fullName = `${GroupClassroomContent.Name} ${group.name}`;
      setPageName(fullName);
    }
    return () => resetPageName();
  }, [group?.name, setPageName, resetPageName]);

  const {
    setCourseId,
    setLessonId,
    setHomeworkMode,
    setLessonMode,
    setHomeworkId,
  } = useExerciseConstructor();

  const { setViewMode } = useLessonSettings();

  useEffect(() => {
    setCourseId(courseId);
    setLessonId(lessonId);
  }, [courseId, lessonId, setCourseId, setLessonId]);

  useEffect(() => {
    setViewMode("preview");

    return () => {
      setViewMode("constructor");
    };
  }, [setViewMode]);

  useEffect(() => {
    const isChaptersAvailable: boolean = lessonChapters.length > 0;

    // Case 0: Lesson does not have chapters
    if (!isChaptersAvailable && !queryChapterId) return;

    // Case 1: Lesson has chapters and chapterId parameter is not provided (when user opens lesson from materials list)
    if (!queryChapterId && isChaptersAvailable) {
      const [firstChapter] = lessonChapters;
      setActiveChapterId(firstChapter.id);
      setSearchParams({ chapterId: firstChapter.id.toString() });
      return;
    }

    const targetChapter = lessonChapters.find(
      (chapter) => chapter.id === Number(queryChapterId)
    );

    // Case 2: Lesson has chapters and chapterId parameter is provided (when user opens direct link to lesson chapter)
    if (queryChapterId && isChaptersAvailable && targetChapter) {
      setActiveChapterId(targetChapter.id);
      setSearchParams({ chapterId: targetChapter.id.toString() });
      return;
    }

    // Case 3: Lesson has chapters and chapterId parameter is provided but chapter with this id does not exist
    if (queryChapterId && isChaptersAvailable && !targetChapter) {
      logError({ message: Content.Chapter.Error.INVALID_LESSON_CHAPTER_ID });
      return;
    }
  }, [lessonChapters, queryChapterId, setSearchParams]);

  const chapterButtonClickHandler = (chapterId: number) => {
    setContentType(ELessonContentType.LESSON);
    setActiveChapterId(chapterId);
    setSearchParams({ chapterId: chapterId.toString() });
    setLessonMode();
  };

  const homeworkButtonClickHandler = () => {
    setContentType(ELessonContentType.HOMEWORK);
    setActiveChapterId(null);
    setHomeworkId(lesson?.homeworkId);
    setHomeworkMode();
  };

  const backClickHandler = () => {
    navigate(
      Route.App.Study.GROUP_CLASSROOM_COURSE.construct(groupId, courseId)
    );
  };

  const pinLessonClickHandler = () => {
    if (!groupId || !courseId) return;
    addLessonToGroup({
      courseId,
      lessonId,
      body: { groupId: Number(groupId) },
    });
  };

  const unpinLessonClickHandler = () => {
    if (!groupId || !courseId) return;

    detachLessonFromGroup({
      courseId,
      lessonId,
      body: { groupId: Number(groupId) },
    });
  };

  const handleStudentSelect = (studentId: number) => {
    setSelectedStudentId(studentId);
  };

  return (
    <LessonContainer>
      {contentType === ELessonContentType.LESSON && (
        <LessonContent
          key={`lesson-${studentId}`}
          activeChapterId={activeChapterId}
          loading={isLoading}
          lesson={lesson}
          lessonChapters={lessonChapters}
          overrideStudentId={studentId}
          studentSwitcherProps={
            group?.memberIds && group.memberIds.length > 1
              ? {
                  studentIds: group.memberIds,
                  selectedStudentId: selectedStudentId || undefined,
                  onStudentSelect: handleStudentSelect,
                  loading: isGroupLoading,
                }
              : undefined
          }
        />
      )}
      {contentType === ELessonContentType.HOMEWORK && (
        <HomeworkContent
          loading={isChaptersLoading}
          homeworkId={lesson?.homeworkId}
          overrideStudentId={studentId}
          studentSwitcherProps={
            group?.memberIds && group.memberIds.length > 1
              ? {
                  studentIds: group.memberIds,
                  selectedStudentId: selectedStudentId || undefined,
                  onStudentSelect: handleStudentSelect,
                  loading: isGroupLoading,
                }
              : undefined
          }
        />
      )}
      <LessonSidebar withHeader>
        <Space
          direction="horizontal"
          size="x-small"
          justify="between"
          align="center"
          fullWidth
        >
          <Button variant="secondary" onClick={backClickHandler} fullWidth>
            {GroupClassroomContent.LessonPreview.Action.BACK}
          </Button>
          {!isUserStudent && (
            <Button
              onClick={
                isLessonPinned ? unpinLessonClickHandler : pinLessonClickHandler
              }
              fullWidth
            >
              {isLessonPinned
                ? GroupClassroomContent.LessonPreview.Action.UNPIN
                : GroupClassroomContent.LessonPreview.Action.PIN}
            </Button>
          )}
        </Space>
        <ChaptersSidebar
          loading={isChaptersLoading}
          lessonChapters={lessonChapters}
          activeChapterId={activeChapterId}
          onChapterClick={chapterButtonClickHandler}
        />
        <HomeworkSidebar
          active={contentType === ELessonContentType.HOMEWORK}
          onButtonClick={homeworkButtonClickHandler}
        />
      </LessonSidebar>
    </LessonContainer>
  );
};
